<script setup lang="ts">
import { useTranslation } from '@/composables';
import { useValidationRules } from '@/composables/useValidationRules';
import {
    useAttrs, computed, ref, onMounted,
} from 'vue';
import type { VTextarea } from 'vuetify/components';

type PropsType = {
    modelValue?: string | null;
    label?: string;
    placeholder?: string;
    variant?: VTextarea['$props']['variant'];
    density?: VTextarea['$props']['density'];
    color?: string;
    clearable?: boolean;
    loading?: boolean;
    isLabelTranslated?: boolean;
    showRequiredVisual?: boolean;
    rows?: number;
    autoGrow?: boolean;
    name?: string;
    dataType?: string;
};

const props = withDefaults(defineProps<PropsType>(), {
    variant: 'outlined',
    density: 'compact',
    clearable: false,
    loading: false,
    isLabelTranslated: true,
    showRequiredVisual: true,
    rows: 3,
    autoGrow: false,
    dataType: 'string',
});

const emit = defineEmits<{
    'update:modelValue': [value: string];
    'click:clear': [];
    'click:append': [];
    'click:prepend': [];
    'click:appendInner': [];
    'click:prependInner': [];
    focus: [event: FocusEvent];
    blur: [event: FocusEvent];
    input: [value: string];
    change: [value: string];
}>();

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

const { t } = useTranslation();

const getLabel = computed((): string => {
    if (!props.label) {
        return '';
    }
    if (props.isLabelTranslated) {
        return t(props.label);
    }

    return props.label;
});

const { rules, isRequired } = useValidationRules(attrs);

const textareaRef = ref<VTextarea | null>(null);

onMounted(() => {
    const textarea = textareaRef.value?.$el?.querySelector('textarea');
    if (textarea) {
        textarea.dataset.type = props.dataType;
    }
});
</script>

<template>
    <VTextarea
        ref="textareaRef"
        :model-value="modelValue"
        :label="getLabel"
        :placeholder="placeholder"
        :variant="variant"
        :density="density"
        :color="color"
        :clearable="clearable"
        :loading="loading"
        :rows="rows"
        :auto-grow="autoGrow"
        :name="name"
        :class="[$attrs.class, { required: isRequired && showRequiredVisual }]"
        v-bind="{
            ...$attrs,
            rules: rules,
        }"
        @update:model-value="emit('update:modelValue', $event)"
        @click:clear="emit('click:clear')"
        @click:append="emit('click:append')"
        @click:prepend="emit('click:prepend')"
        @click:append-inner="emit('click:appendInner')"
        @click:prepend-inner="emit('click:prependInner')"
        @focus="emit('focus', $event)"
        @blur="emit('blur', $event)"
        @input="emit('input', $event)"
        @change="emit('change', $event)"
    >
        <template
            v-if="$slots.prepend"
            #prepend
        >
            <slot name="prepend" />
        </template>

        <template
            v-if="$slots.append"
            #append
        >
            <slot name="append" />
        </template>

        <template
            v-if="$slots.prependInner"
            #prepend-inner
        >
            <slot name="prepend-inner" />
        </template>

        <template
            v-if="$slots.appendInner"
            #append-inner
        >
            <slot name="append-inner" />
        </template>

        <template
            v-if="$slots.label"
            #label
        >
            <slot name="label" />
        </template>

        <template
            v-if="$slots.message"
            #message="{ message }"
        >
            <slot
                name="message"
                :message="message"
            />
        </template>

        <template
            v-if="$slots.details"
            #details
        >
            <slot name="details" />
        </template>
    </VTextarea>
</template>
